"use client";

import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { Link, usePathname } from "@/i18n/navigation";

const ITEMS = [
  { hash: "services", key: "services" },
  { hash: "portfolio", key: "portfolio" },
  { hash: "contact", key: "contact" },
] as const;

export function NavLinks({
  orientation = "horizontal",
  onNavigate,
}: {
  orientation?: "horizontal" | "vertical";
  onNavigate?: () => void;
}) {
  const t = useTranslations("nav");
  const pathname = usePathname();
  const isHome = pathname === "/";
  const [activeHash, setActiveHash] = useState<string | null>(null);

  useEffect(() => {
    if (!isHome) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveHash(entry.target.id);
          }
        }
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 },
    );
    ITEMS.forEach((item) => {
      const el = document.getElementById(item.hash);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [isHome]);

  const containerClass =
    orientation === "horizontal"
      ? "flex items-center gap-1"
      : "flex flex-col gap-1";

  return (
    <nav className={containerClass} aria-label="Primary">
      {ITEMS.map((item) => {
        const isActive = isHome && activeHash === item.hash;
        return (
          <Link
            key={item.hash}
            href={{ pathname: "/", hash: item.hash }}
            onClick={onNavigate}
            className={`group relative rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              orientation === "vertical" ? "text-base" : ""
            } ${
              isActive
                ? "text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <span className="relative">
              {t(item.key)}
              <span
                aria-hidden
                className={`absolute -bottom-1 left-0 right-0 h-px origin-center bg-gradient-to-r from-transparent via-aurora-cyan to-transparent transition-transform duration-300 ${
                  isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                }`}
              />
            </span>
          </Link>
        );
      })}
    </nav>
  );
}
